package com.ijoin.ui.design

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.RowScope
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material.icons.filled.Check
import androidx.compose.material.icons.filled.Favorite
import androidx.compose.material.icons.outlined.FavoriteBorder
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.FilterChipDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.ColorScheme
import androidx.compose.material3.lightColorScheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Switch
import androidx.compose.material3.SwitchDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog

val EventCardAccent = Color(0xFF1F7A8C)
val EventCardAccentDark = Color(0xFF0F4E57)
val EventCardAccentSoft = Color(0xFFE1F2F4)
val EventCardBackground = Color(0xFFF7F4EF)
val EventCardSurface = Color(0xFFFFFBF7)
val EventCardOutline = Color(0xFFE7E0D8)
val EventCardWarm = Color(0xFFB45309)
val EventCardWarmSoft = Color(0xFFFFF1E1)
val EventCardWarmLight = Color(0xFFD97706)
val EventCardAlert = Color(0xFFE53935)
val EventCardRailContent = Color(0xFF3C3A36)
val EventCardRailWidth = 176.dp
val EventCardCorner = 20.dp
val EventCardCompactCorner = 16.dp
val EventCardInputCorner = 14.dp
val EventCardPillCorner = 999.dp
val EventCardBadgeBackground = EventCardWarmSoft
val EventCardBadgeText = EventCardWarm
val EventCardDateBadgeBackground = EventCardSurface
val EventCardDateBadgeText = EventCardAccentDark
val EventCardPrimaryButton = EventCardAccent
val EventCardPrimaryButtonText = Color.White
val EventCardPriceFree = EventCardAccentDark
val EventCardPricePaid = EventCardWarm
val EventCardSurfaceVariant = EventCardAccentSoft

fun eventBackgroundBrush(): Brush {
    return Brush.verticalGradient(listOf(EventCardBackground, Color.White))
}

fun eventColorScheme(): ColorScheme = lightColorScheme(
    primary = EventCardAccent,
    onPrimary = EventCardPrimaryButtonText,
    primaryContainer = EventCardAccentSoft,
    onPrimaryContainer = EventCardAccentDark,
    secondary = EventCardWarm,
    onSecondary = EventCardPrimaryButtonText,
    secondaryContainer = EventCardWarmSoft,
    onSecondaryContainer = EventCardWarm,
    background = EventCardBackground,
    onBackground = EventCardRailContent,
    surface = EventCardSurface,
    onSurface = EventCardRailContent,
    surfaceVariant = EventCardSurfaceVariant,
    onSurfaceVariant = EventCardRailContent.copy(alpha = 0.75f),
    outline = EventCardOutline,
    error = EventCardAlert,
    onError = EventCardPrimaryButtonText,
    errorContainer = EventCardAlert.copy(alpha = 0.12f),
    onErrorContainer = EventCardAlert,
    surfaceTint = EventCardAccent
)

fun eventTicketGradient(isHighlighted: Boolean): Brush {
    val colors = if (isHighlighted) {
        listOf(EventCardAccent, EventCardAccentDark)
    } else {
        listOf(EventCardWarm, EventCardWarmLight)
    }
    return Brush.horizontalGradient(colors)
}

@Composable
fun eventFilterChipColors() = FilterChipDefaults.filterChipColors(
    selectedContainerColor = EventCardAccentSoft,
    selectedLabelColor = EventCardAccentDark,
    containerColor = EventCardSurface,
    labelColor = MaterialTheme.colorScheme.onSurfaceVariant
)

@Composable
fun eventFilterChipBorder(selected: Boolean, enabled: Boolean = true): BorderStroke? {
    val baseColor = if (selected) EventCardAccent else EventCardOutline
    val alpha = if (enabled) 1f else 0.5f
    return BorderStroke(1.dp, baseColor.copy(alpha = alpha))
}

@Composable
fun eventOutlinedFieldColors() = OutlinedTextFieldDefaults.colors(
    unfocusedBorderColor = EventCardOutline,
    focusedBorderColor = EventCardAccent,
    cursorColor = EventCardAccent,
    unfocusedContainerColor = EventCardSurface,
    focusedContainerColor = EventCardSurface,
    focusedLabelColor = EventCardAccentDark,
    focusedLeadingIconColor = EventCardAccentDark,
    focusedTrailingIconColor = EventCardAccentDark,
    focusedTextColor = MaterialTheme.colorScheme.onSurface,
    focusedPlaceholderColor = MaterialTheme.colorScheme.onSurfaceVariant,
    focusedSupportingTextColor = MaterialTheme.colorScheme.onSurfaceVariant,
    unfocusedLabelColor = MaterialTheme.colorScheme.onSurfaceVariant,
    unfocusedLeadingIconColor = MaterialTheme.colorScheme.onSurfaceVariant,
    unfocusedTrailingIconColor = MaterialTheme.colorScheme.onSurfaceVariant,
    unfocusedPlaceholderColor = MaterialTheme.colorScheme.onSurfaceVariant,
    disabledBorderColor = EventCardOutline,
    disabledTextColor = MaterialTheme.colorScheme.onSurface,
    disabledLeadingIconColor = MaterialTheme.colorScheme.onSurfaceVariant,
    disabledTrailingIconColor = MaterialTheme.colorScheme.onSurfaceVariant,
    disabledLabelColor = MaterialTheme.colorScheme.onSurfaceVariant,
    disabledPlaceholderColor = MaterialTheme.colorScheme.onSurfaceVariant,
    errorBorderColor = MaterialTheme.colorScheme.error,
    errorCursorColor = MaterialTheme.colorScheme.error,
    errorLabelColor = MaterialTheme.colorScheme.error,
    errorLeadingIconColor = MaterialTheme.colorScheme.error,
    errorTrailingIconColor = MaterialTheme.colorScheme.error,
    errorSupportingTextColor = MaterialTheme.colorScheme.error
)

@Composable
fun eventSwitchColors() = SwitchDefaults.colors(
    checkedThumbColor = EventCardSurface,
    checkedTrackColor = EventCardAccent,
    checkedBorderColor = EventCardAccent,
    uncheckedThumbColor = EventCardSurface,
    uncheckedTrackColor = EventCardOutline,
    uncheckedBorderColor = EventCardOutline
)

@Composable
fun EventSurfaceCard(
    modifier: Modifier = Modifier,
    cornerRadius: Dp = EventCardCorner,
    backgroundColor: Color = EventCardSurface,
    borderColor: Color = EventCardOutline,
    shadowElevation: Dp = 2.dp,
    contentPadding: PaddingValues = PaddingValues(16.dp),
    verticalArrangement: Arrangement.Vertical = Arrangement.spacedBy(12.dp),
    content: @Composable ColumnScope.() -> Unit
) {
    Surface(
        modifier = modifier,
        color = backgroundColor,
        shape = RoundedCornerShape(cornerRadius),
        border = BorderStroke(1.dp, borderColor),
        shadowElevation = shadowElevation
    ) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .padding(contentPadding),
            verticalArrangement = verticalArrangement,
            content = content
        )
    }
}

@Composable
fun EventDialog(
    onDismissRequest: () -> Unit,
    modifier: Modifier = Modifier,
    contentPadding: PaddingValues = PaddingValues(20.dp),
    content: @Composable ColumnScope.() -> Unit
) {
    Dialog(onDismissRequest = onDismissRequest) {
        EventSurfaceCard(
            modifier = modifier.fillMaxWidth(),
            cornerRadius = EventCardCorner,
            backgroundColor = EventCardSurface,
            borderColor = EventCardOutline,
            contentPadding = contentPadding,
            verticalArrangement = Arrangement.spacedBy(12.dp),
            content = content
        )
    }
}

@Composable
fun EventSectionCard(
    title: String? = null,
    modifier: Modifier = Modifier,
    content: @Composable ColumnScope.() -> Unit
) {
    Surface(
        modifier = modifier.fillMaxWidth(),
        color = EventCardSurface,
        shape = RoundedCornerShape(EventCardCorner),
        border = BorderStroke(1.dp, EventCardOutline),
        shadowElevation = 2.dp
    ) {
        Column(
            modifier = Modifier.padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            if (!title.isNullOrBlank()) {
                Text(
                    text = title,
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.SemiBold
                )
            }
            content()
        }
    }
}

@Composable
fun EventInfoCard(
    title: String,
    icon: ImageVector,
    modifier: Modifier = Modifier,
    backgroundColor: Color = EventCardAccentSoft,
    borderColor: Color = EventCardOutline,
    content: @Composable ColumnScope.() -> Unit = {}
) {
    EventSurfaceCard(
        modifier = modifier,
        cornerRadius = EventCardCompactCorner,
        backgroundColor = backgroundColor,
        borderColor = borderColor
    ) {
        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            Icon(
                imageVector = icon,
                contentDescription = null,
                tint = EventCardAccentDark,
                modifier = Modifier.size(20.dp)
            )
            Text(
                text = title,
                style = MaterialTheme.typography.labelLarge,
                fontWeight = FontWeight.SemiBold,
                color = EventCardAccentDark
            )
        }
        content()
    }
}

@Composable
fun EventSegmentButton(
    text: String,
    selected: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    leadingIcon: ImageVector? = null
) {
    val borderColor = if (selected) EventCardAccent else EventCardOutline
    val containerColor = if (selected) EventCardAccentSoft else EventCardSurface
    val contentColor = if (selected) EventCardAccentDark else MaterialTheme.colorScheme.onSurface
    OutlinedButton(
        onClick = onClick,
        modifier = modifier,
        colors = ButtonDefaults.outlinedButtonColors(
            containerColor = containerColor,
            contentColor = contentColor
        ),
        border = BorderStroke(if (selected) 2.dp else 1.dp, borderColor),
        shape = RoundedCornerShape(EventCardInputCorner)
    ) {
        if (leadingIcon != null) {
            Icon(
                imageVector = leadingIcon,
                contentDescription = null,
                modifier = Modifier.size(16.dp)
            )
            Spacer(modifier = Modifier.width(6.dp))
        }
        Text(
            text = text,
            style = MaterialTheme.typography.labelLarge,
            fontWeight = FontWeight.SemiBold
        )
    }
}

@Composable
fun EventSelectableCard(
    selected: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    contentPadding: PaddingValues = PaddingValues(16.dp),
    content: @Composable RowScope.() -> Unit
) {
    val borderColor = if (selected) EventCardAccent else EventCardOutline
    val containerColor = if (selected) EventCardAccentSoft else EventCardSurface
    Surface(
        modifier = modifier,
        color = containerColor,
        shape = RoundedCornerShape(EventCardCompactCorner),
        border = BorderStroke(1.dp, borderColor),
        shadowElevation = if (selected) 2.dp else 1.dp
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .clickable(onClick = onClick)
                .padding(contentPadding),
            verticalAlignment = Alignment.CenterVertically,
            content = content
        )
    }
}

@Composable
fun EventToggleCard(
    title: String,
    description: String,
    checked: Boolean,
    onCheckedChange: (Boolean) -> Unit,
    modifier: Modifier = Modifier,
    switchModifier: Modifier = Modifier
) {
    EventSurfaceCard(
        modifier = modifier,
        cornerRadius = EventCardCompactCorner,
        backgroundColor = if (checked) EventCardAccentSoft else EventCardSurface,
        borderColor = EventCardOutline
    ) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            Column(modifier = Modifier.weight(1f)) {
                Text(
                    text = title,
                    style = MaterialTheme.typography.bodyLarge,
                    fontWeight = FontWeight.SemiBold
                )
                Text(
                    text = description,
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
            Switch(
                checked = checked,
                onCheckedChange = onCheckedChange,
                colors = eventSwitchColors(),
                modifier = switchModifier
            )
        }
    }
}

@Composable
fun CalendarNavButton(
    onClick: () -> Unit,
    enabled: Boolean,
    icon: ImageVector,
    contentDescription: String
) {
    val tint = if (enabled) EventCardAccentDark else MaterialTheme.colorScheme.onSurfaceVariant
    Surface(
        color = if (enabled) EventCardAccentSoft else EventCardOutline.copy(alpha = 0.4f),
        shape = CircleShape,
        border = BorderStroke(1.dp, EventCardOutline)
    ) {
        IconButton(
            onClick = onClick,
            enabled = enabled,
            modifier = Modifier.size(36.dp)
        ) {
            Icon(
                imageVector = icon,
                contentDescription = contentDescription,
                tint = tint
            )
        }
    }
}

@Composable
fun EventRoundIconButton(
    icon: ImageVector,
    contentDescription: String?,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    showIndicator: Boolean = false
) {
    Surface(
        color = EventCardSurface,
        shape = CircleShape,
        border = BorderStroke(1.dp, EventCardOutline),
        shadowElevation = 2.dp
    ) {
        IconButton(
            onClick = onClick,
            modifier = modifier.size(44.dp)
        ) {
            Box(contentAlignment = Alignment.Center) {
                Icon(
                    imageVector = icon,
                    contentDescription = contentDescription,
                    tint = EventCardAccentDark
                )
                if (showIndicator) {
                    Box(
                        modifier = Modifier
                            .align(Alignment.TopEnd)
                            .padding(top = 2.dp, end = 2.dp)
                            .size(10.dp)
                            .clip(CircleShape)
                            .background(EventCardAlert)
                    )
                }
            }
        }
    }
}

@Composable
fun EventTopBar(
    title: String,
    actionIcon: ImageVector,
    actionContentDescription: String?,
    onActionClick: () -> Unit,
    showActionIndicator: Boolean,
    modifier: Modifier = Modifier,
    actionModifier: Modifier = Modifier
) {
    Row(
        modifier = modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        Text(
            text = title,
            style = MaterialTheme.typography.titleLarge,
            fontWeight = FontWeight.Bold,
            color = EventCardAccentDark,
            maxLines = 1,
            overflow = TextOverflow.Ellipsis
        )
        EventRoundIconButton(
            icon = actionIcon,
            contentDescription = actionContentDescription,
            onClick = onActionClick,
            modifier = actionModifier,
            showIndicator = showActionIndicator
        )
    }
}

@Composable
fun EventSearchField(
    value: String,
    onValueChange: (String) -> Unit,
    placeholder: String,
    modifier: Modifier = Modifier,
    leadingIcon: ImageVector? = null
) {
    OutlinedTextField(
        value = value,
        onValueChange = onValueChange,
        modifier = modifier.fillMaxWidth(),
        placeholder = {
            Text(
                text = placeholder,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        },
        leadingIcon = leadingIcon?.let { icon ->
            {
                Icon(
                    imageVector = icon,
                    contentDescription = null,
                    tint = MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        },
        shape = RoundedCornerShape(EventCardInputCorner),
        colors = eventOutlinedFieldColors(),
        singleLine = true
    )
}

@Composable
fun EventPill(
    text: String,
    backgroundColor: Color,
    textColor: Color,
    modifier: Modifier = Modifier,
    borderColor: Color? = null,
    textStyle: TextStyle = MaterialTheme.typography.labelSmall,
    fontWeight: FontWeight = FontWeight.SemiBold,
    contentPadding: PaddingValues = PaddingValues(horizontal = 12.dp, vertical = 6.dp)
) {
    Surface(
        modifier = modifier,
        color = backgroundColor,
        shape = RoundedCornerShape(EventCardPillCorner),
        border = borderColor?.let { BorderStroke(1.dp, it) }
    ) {
        Text(
            text = text,
            style = textStyle,
            fontWeight = fontWeight,
            color = textColor,
            modifier = Modifier.padding(contentPadding)
        )
    }
}

@Composable
fun EventPrimaryButton(
    text: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    isLoading: Boolean = false,
    leadingIcon: ImageVector? = null,
    contentPadding: PaddingValues = ButtonDefaults.ContentPadding
) {
    Button(
        onClick = onClick,
        modifier = modifier,
        enabled = enabled && !isLoading,
        colors = ButtonDefaults.buttonColors(
            containerColor = EventCardPrimaryButton,
            contentColor = EventCardPrimaryButtonText
        ),
        shape = RoundedCornerShape(EventCardInputCorner),
        contentPadding = contentPadding
    ) {
        if (isLoading) {
            CircularProgressIndicator(
                modifier = Modifier.size(18.dp),
                strokeWidth = 2.dp,
                color = EventCardPrimaryButtonText
            )
        } else {
            if (leadingIcon != null) {
                Icon(
                    imageVector = leadingIcon,
                    contentDescription = null,
                    modifier = Modifier.size(18.dp)
                )
                Spacer(modifier = Modifier.width(8.dp))
            }
            Text(
                text = text,
                style = MaterialTheme.typography.titleMedium
            )
        }
    }
}

@Composable
fun EventOutlineButton(
    text: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    isLoading: Boolean = false,
    leadingIcon: ImageVector? = null,
    contentPadding: PaddingValues = ButtonDefaults.ContentPadding
) {
    OutlinedButton(
        onClick = onClick,
        modifier = modifier,
        enabled = enabled && !isLoading,
        colors = ButtonDefaults.outlinedButtonColors(
            containerColor = EventCardSurface,
            contentColor = EventCardAccentDark
        ),
        border = BorderStroke(1.dp, EventCardOutline),
        shape = RoundedCornerShape(EventCardInputCorner),
        contentPadding = contentPadding
    ) {
        if (isLoading) {
            CircularProgressIndicator(
                modifier = Modifier.size(18.dp),
                strokeWidth = 2.dp,
                color = EventCardAccentDark
            )
        } else {
            if (leadingIcon != null) {
                Icon(
                    imageVector = leadingIcon,
                    contentDescription = null,
                    modifier = Modifier.size(18.dp)
                )
                Spacer(modifier = Modifier.width(8.dp))
            }
            Text(
                text = text,
                style = MaterialTheme.typography.titleMedium
            )
        }
    }
}

@Composable
fun EventTextAction(
    text: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    leadingIcon: ImageVector? = null,
    trailingIcon: ImageVector? = null,
    contentColor: Color = EventCardAccentDark,
    isLoading: Boolean = false
) {
    TextButton(
        onClick = onClick,
        modifier = modifier,
        enabled = enabled && !isLoading,
        colors = ButtonDefaults.textButtonColors(contentColor = contentColor)
    ) {
        if (isLoading) {
            CircularProgressIndicator(
                modifier = Modifier.size(16.dp),
                strokeWidth = 2.dp,
                color = contentColor
            )
        } else {
            if (leadingIcon != null) {
                Icon(
                    imageVector = leadingIcon,
                    contentDescription = null,
                    modifier = Modifier.size(18.dp)
                )
                Spacer(modifier = Modifier.width(6.dp))
            }
            Text(
                text = text,
                style = MaterialTheme.typography.labelLarge,
                fontWeight = FontWeight.SemiBold
            )
            if (trailingIcon != null) {
                Spacer(modifier = Modifier.width(6.dp))
                Icon(
                    imageVector = trailingIcon,
                    contentDescription = null,
                    modifier = Modifier.size(18.dp)
                )
            }
        }
    }
}

@Composable
fun EventDangerButton(
    text: String,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true,
    isLoading: Boolean = false,
    leadingIcon: ImageVector? = null,
    contentPadding: PaddingValues = ButtonDefaults.ContentPadding
) {
    Button(
        onClick = onClick,
        modifier = modifier,
        enabled = enabled && !isLoading,
        colors = ButtonDefaults.buttonColors(
            containerColor = EventCardAlert,
            contentColor = EventCardPrimaryButtonText
        ),
        shape = RoundedCornerShape(EventCardInputCorner),
        contentPadding = contentPadding
    ) {
        if (isLoading) {
            CircularProgressIndicator(
                modifier = Modifier.size(18.dp),
                strokeWidth = 2.dp,
                color = EventCardPrimaryButtonText
            )
        } else {
            if (leadingIcon != null) {
                Icon(
                    imageVector = leadingIcon,
                    contentDescription = null,
                    modifier = Modifier.size(18.dp)
                )
                Spacer(modifier = Modifier.width(8.dp))
            }
            Text(
                text = text,
                style = MaterialTheme.typography.titleMedium
            )
        }
    }
}

@Composable
fun EventStepperButton(
    onClick: () -> Unit,
    enabled: Boolean,
    icon: ImageVector,
    contentDescription: String,
    modifier: Modifier = Modifier,
    size: Dp = 36.dp
) {
    val tint = if (enabled) EventCardAccentDark else MaterialTheme.colorScheme.onSurfaceVariant
    Surface(
        color = if (enabled) EventCardAccentSoft else EventCardOutline.copy(alpha = 0.4f),
        shape = CircleShape,
        border = BorderStroke(1.dp, EventCardOutline),
        modifier = modifier
    ) {
        IconButton(
            onClick = onClick,
            enabled = enabled,
            modifier = Modifier.size(size)
        ) {
            Icon(
                imageVector = icon,
                contentDescription = contentDescription,
                tint = tint
            )
        }
    }
}

@Composable
fun EventWizardTopBar(
    title: String,
    backContentDescription: String,
    onBackClick: () -> Unit,
    showSaveDraft: Boolean,
    saveDraftLabel: String,
    onSaveDraftClick: () -> Unit,
    modifier: Modifier = Modifier
) {
    Surface(
        modifier = modifier.fillMaxWidth(),
        color = EventCardSurface,
        border = BorderStroke(1.dp, EventCardOutline),
        shadowElevation = 2.dp
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(horizontal = 16.dp, vertical = 12.dp),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically
        ) {
            EventRoundIconButton(
                icon = Icons.Default.ArrowBack,
                contentDescription = backContentDescription,
                onClick = onBackClick
            )
            Text(
                text = title,
                style = MaterialTheme.typography.titleLarge,
                fontWeight = FontWeight.Bold,
                color = EventCardAccentDark,
                modifier = Modifier
                    .weight(1f)
                    .padding(horizontal = 16.dp)
            )
            if (showSaveDraft) {
                EventTextAction(
                    text = saveDraftLabel,
                    onClick = onSaveDraftClick
                )
            } else {
                Spacer(modifier = Modifier.size(44.dp))
            }
        }
    }
}

@Composable
fun EventWizardStepIndicator(
    currentStep: Int,
    totalSteps: Int,
    modifier: Modifier = Modifier
) {
    Row(
        modifier = modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.Center,
        verticalAlignment = Alignment.CenterVertically
    ) {
        repeat(totalSteps) { index ->
            val step = index + 1
            val isCompleted = step < currentStep
            val isCurrent = step == currentStep
            val stepColor = when {
                isCompleted -> EventCardAccent
                isCurrent -> EventCardAccentDark
                else -> EventCardOutline
            }
            val textColor = if (isCompleted || isCurrent) Color.White else MaterialTheme.colorScheme.onSurfaceVariant

            Box(
                modifier = Modifier
                    .size(if (isCurrent) 38.dp else 32.dp)
                    .clip(CircleShape)
                    .background(stepColor),
                contentAlignment = Alignment.Center
            ) {
                if (isCompleted) {
                    Icon(
                        imageVector = Icons.Default.Check,
                        contentDescription = null,
                        tint = Color.White,
                        modifier = Modifier.size(18.dp)
                    )
                } else {
                    Text(
                        text = step.toString(),
                        style = MaterialTheme.typography.bodyMedium,
                        fontWeight = if (isCurrent) FontWeight.Bold else FontWeight.Medium,
                        color = textColor
                    )
                }
            }

            if (index < totalSteps - 1) {
                Box(
                    modifier = Modifier
                        .width(36.dp)
                        .height(2.dp)
                        .background(if (isCompleted) EventCardAccent else EventCardOutline)
                )
            }
        }
    }
}

@Composable
fun EventSectionHeader(
    title: String,
    subtitle: String,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(4.dp)
    ) {
        Text(
            text = title,
            style = MaterialTheme.typography.titleLarge,
            fontWeight = FontWeight.Bold,
            color = EventCardAccentDark
        )
        Text(
            text = subtitle,
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
    }
}

@Composable
fun EventSectionTitle(
    title: String,
    modifier: Modifier = Modifier
) {
    Text(
        text = title,
        style = MaterialTheme.typography.titleLarge,
        fontWeight = FontWeight.Bold,
        color = EventCardAccentDark,
        modifier = modifier
    )
}

@Composable
fun EventFeedSectionHeader(
    title: String,
    subtitle: String,
    showFavorites: Boolean,
    onShowFeed: () -> Unit,
    onShowFavorites: () -> Unit,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(4.dp)
    ) {
        Box(modifier = Modifier.fillMaxWidth()) {
            Text(
                text = title,
                style = MaterialTheme.typography.titleLarge,
                fontWeight = FontWeight.Bold,
                color = if (showFavorites) {
                    MaterialTheme.colorScheme.onSurfaceVariant
                } else {
                    EventCardAccentDark
                },
                modifier = Modifier
                    .align(Alignment.CenterStart)
                    .padding(end = 48.dp)
                    .clickable { onShowFeed() },
                maxLines = 1,
                overflow = TextOverflow.Ellipsis
            )
            IconButton(
                onClick = onShowFavorites,
                modifier = Modifier.align(Alignment.CenterEnd)
            ) {
                Icon(
                    imageVector = if (showFavorites) Icons.Filled.Favorite else Icons.Outlined.FavoriteBorder,
                    contentDescription = null,
                    tint = if (showFavorites) EventCardAccent else MaterialTheme.colorScheme.onSurfaceVariant
                )
            }
        }
        Text(
            text = subtitle,
            style = MaterialTheme.typography.bodyMedium,
            color = MaterialTheme.colorScheme.onSurfaceVariant
        )
    }
}
